import { uiText } from './text.js';
import './refusal-banner.css';

/** Which line the banner says: a refusal, or the place the player walks into. */
export type BannerTone = 'refusal' | 'place';

/** How long a refusal stays on screen after the last time it was said. */
const SHOWN_MS = 2200;

/**
 * Why an action did not happen (a spell still cooling, a chest out of reach,
 * no mount worn): one line over the world that fades by itself, never a line
 * of the chat, which is the players' to read. The same refusal said again
 * only keeps it up a little longer, so mashing a key never stacks lines.
 * The `place` variant announces the place the player walks into (SHE-139):
 * the same banner in another colour.
 */
export class RefusalBanner {
  private root: HTMLElement | undefined;
  private timer: number | undefined;

  constructor(private readonly tone: BannerTone = 'refusal') {}

  show(text: string): void {
    const line = uiText(text);
    const root = this.root ?? this.mount();
    if (root.hidden || root.textContent !== line) {
      root.textContent = line;
      root.hidden = false;
      // Restart the pop-in: a new reason should catch the eye like the first.
      root.classList.remove('refusal-banner--in');
      requestAnimationFrame(() => {
        root.classList.add('refusal-banner--in');
      });
    }
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      root.hidden = true;
    }, SHOWN_MS);
  }

  destroy(): void {
    window.clearTimeout(this.timer);
    this.root?.remove();
    this.root = undefined;
  }

  private mount(): HTMLElement {
    const root = document.createElement('div');
    root.className = 'refusal-banner';
    if (this.tone === 'place') root.classList.add('refusal-banner--place');
    root.hidden = true;
    root.setAttribute('role', 'status');
    root.setAttribute('aria-live', 'polite');
    document.body.append(root);
    this.root = root;
    return root;
  }
}
